import { useEffect, useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Robust ScrollToTop Component
 * Disables browser auto scroll restoration and forces window and all root containers
 * to scroll to the top immediately upon every route change.
 */
const ScrollToTop = () => {
  const { pathname, search, hash } = useLocation();

  // Prevent the browser from automatically restoring old scroll positions
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);

  const scrollToTopNow = () => {
    // 1. Direct window scroll (instant)
    try {
      window.scrollTo(0, 0);
    } catch (e) {
      window.scrollTo(0, 0);
    }

    // 2. Document element & body scroll
    if (document.documentElement) {
      document.documentElement.scrollTop = 0;
      document.documentElement.scrollLeft = 0;
    }
    if (document.body) {
      document.body.scrollTop = 0;
      document.body.scrollLeft = 0;
    }

    // 3. Potential inner root wrappers
    const rootEl = document.getElementById("root");
    if (rootEl) {
      rootEl.scrollTop = 0;
    }

    const wrapperEl = document.getElementById("wrapper");
    if (wrapperEl) {
      wrapperEl.scrollTop = 0;
    }

    const mainEl = document.getElementById("main");
    if (mainEl) {
      mainEl.scrollTop = 0;
    }
  };

  // Immediate layout execution before paint
  useLayoutEffect(() => {
    scrollToTopNow();
  }, [pathname, search, hash]);

  // Delayed execution to handle async rendering, image loading, or state updates
  useEffect(() => {
    scrollToTopNow();

    const frameId = requestAnimationFrame(() => {
      scrollToTopNow();
    });

    const timer1 = setTimeout(() => {
      scrollToTopNow();
    }, 20);

    const timer2 = setTimeout(() => {
      scrollToTopNow();
    }, 100);

    return () => {
      cancelAnimationFrame(frameId);
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, [pathname, search, hash]);

  return null;
};

export default ScrollToTop;
